import { getCookie } from "../cookies.ts";
import { singleton } from "../memoize.ts";
import { parseUrl } from "../url/url.ts";
import { isTrackableVisitor } from "../visitor/isSupportedBrowser.ts";
import {
  clientIdCookieHeader,
  clientIdParam,
  isSolvariId,
  pickClientId,
} from "./clientId.ts";
import { getSolvariSessionId, sessionParam } from "./session.ts";

type CarriedIdentity = {
  queryClientId: string | null;
  refererClientId: string | null;
  sessionUuid: string | null;
};

type Identity = {
  clientId: string | null;
  from: string;
  sessionUuid: string | null;
};

// Read once per page load: a later navigation must not re-adopt the landing params.
// Query + referer are attacker-supplied → the shape check lives in the shared core.
const carriedIdentity = singleton((): CarriedIdentity => {
  const queryParams = new URLSearchParams(window.location.search);
  const refererParams = parseUrl(document.referrer)?.searchParams;

  return {
    queryClientId: queryParams.get(clientIdParam),
    refererClientId: refererParams?.get(clientIdParam) ?? null,
    sessionUuid:
      [queryParams.get(sessionParam), refererParams?.get(sessionParam)].find(
        (candidate) => isSolvariId(candidate),
      ) ?? null,
  };
});

function getSolvariClientId(): string | null {
  const clientId = getCookie(clientIdParam, false);
  return isSolvariId(clientId) ? clientId : null;
}

// UUID-checked on write: callers include `postMessage` values from any origin →
// a `;`-bearing value would inject cookie attributes.
function storeSolvariClientId(uuid: string): void {
  if (!isSolvariId(uuid)) {
    return;
  }

  // Not `setCookie`: it cannot emit the `Domain` attribute the cross-subdomain scope needs.
  document.cookie = clientIdCookieHeader(uuid, window.location.host);
}

// Gate precedes minting → bot gets no cookie. null propagates: all downstream no-ops.
function resolveClientId(): string | null {
  if (!isTrackableVisitor()) {
    return null;
  }

  const { queryClientId, refererClientId } = carriedIdentity();
  const clientId = pickClientId({
    cookieClientId: getSolvariClientId(),
    queryClientId,
    refererClientId,
  });
  storeSolvariClientId(clientId);

  return clientId;
}

function currentIdentity(): Identity {
  return {
    clientId: getSolvariClientId(),
    from: window.location.href,
    sessionUuid: getSolvariSessionId(),
  };
}

export type { CarriedIdentity, Identity };
export {
  carriedIdentity,
  currentIdentity,
  getSolvariClientId,
  resolveClientId,
  storeSolvariClientId,
};
